import { Stack, Text } from "@certn/cascadia";
import {
  ChecksMonitoredTab,
  formatMonitoringDate,
  MonitorStatusTag,
} from "@certn/monitoring";
import {
  Container,
  Divider,
  Layout,
  Loader,
  SecondaryTopNav,
  SectionMessage,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { AppHeader } from "../../components/app-header/AppHeader";
import { ApplicantActivityLogTab } from "./ApplicantActivityLogTab";
import { useApplicantMonitoringClientDetail } from "./useMonitoringApi";

export function MonitoringClientDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerAccountId } = useParams();
  const [tab, setTab] = useState("checks");

  const {
    data: detail,
    isLoading,
    error,
  } = useApplicantMonitoringClientDetail(customerAccountId);

  if (isLoading) {
    return (
      <div
        className={css({
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        })}
      >
        <Loader />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <Layout layout="applicant/narrow" header={<AppHeader />}>
        <Container>
          <SectionMessage
            type="danger"
            testID="monitoring-client-detail-error"
            sectionTitle={t("Unable to load monitoring details")}
          >
            {t("Please try again later.")}
          </SectionMessage>
        </Container>
      </Layout>
    );
  }

  const statusDate =
    detail.monitor_status_rollup === "PAUSED" && detail.monitoring_paused_on
      ? detail.monitoring_paused_on
      : detail.monitoring_started_on;

  const statusLine =
    detail.monitor_status_rollup === "PAUSED" && detail.monitoring_paused_on
      ? t("Paused monitoring on {{date}}", {
          date: formatMonitoringDate(statusDate),
        })
      : t("Started monitoring on {{date}}", {
          date: formatMonitoringDate(statusDate),
        });

  return (
    <Layout
      layout="wide"
      backgroundColor="surface-sunken"
      header={
        <>
          <AppHeader />
          <SecondaryTopNav
            leftIcon="arrow_back"
            onleftIconOnPress={() => navigate("/")}
            testID="monitoring-client-secondary-top-nav"
            titleArea={{
              title: detail.company_name,
            }}
          />
          <Divider />
        </>
      }
    >
      <Container layout="full-width" className={styles.pageContainer}>
        <Stack gap="space-4">
          <div className={styles.pageHeader}>
            <Text style="global/heading-large">{detail.company_name}</Text>
            <div className={styles.metaRow}>
              <MonitorStatusTag status={detail.monitor_status_rollup} />
              <Text style="form/body-secondary" color="text-weak">
                {statusLine}
              </Text>
            </div>
          </div>

          <Tabs
            selectedKey={tab}
            onSelectionChange={(key) => setTab(String(key))}
            aria-label={t("Monitoring detail tabs")}
            testID="monitoring-client-detail-tabs"
          >
            <TabList aria-label={t("Monitoring detail tabs")}>
              <Tab id="checks" testID="monitoring-checks-tab">
                {t("Checks monitored")}
              </Tab>
              <Tab id="activity" testID="monitoring-activity-tab">
                {t("Monitoring activity")}
              </Tab>
            </TabList>
            <TabPanel id="checks">
              <ChecksMonitoredTab
                enrollments={detail.enrollments ?? []}
                description={t(
                  "Below is the status and latest results for checks monitored by your employer."
                )}
                emptyMessage={t("No checks are being monitored for you yet.")}
                renderCaseLink={(shortId) => shortId}
              />
            </TabPanel>
            <TabPanel id="activity">
              {tab === "activity" ? (
                <ApplicantActivityLogTab
                  customerAccountId={customerAccountId}
                />
              ) : null}
            </TabPanel>
          </Tabs>
        </Stack>
      </Container>
    </Layout>
  );
}

const styles = {
  pageContainer: css({
    paddingTop: "var(--space-6)",
    paddingBottom: "var(--space-10)",
  }),
  pageHeader: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  metaRow: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-3)",
    flexWrap: "wrap",
  }),
};
